import { useState } from "react";
import { useNavigate } from "react-router";
import { Plus, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useJobs, useUsage, useArchiveJob, type ProcessingJobList } from "@/lib/api";
import { ProjectContextMenu, type MenuPosition } from "@/components/ProjectContextMenu";
import { ProjectInfoModal } from "@/components/ProjectInfoModal";
import { RenameProjectDialog } from "@/components/RenameProjectDialog";
import { DeleteProjectDialog } from "@/components/DeleteProjectDialog";
import { SidebarProjectItem } from "./SidebarProjectItem";

interface SidebarProps {
  selectedJobPosition: number | null;
  onSelectJob: (position: number) => void;
  onNewProject: () => void;
}

export function Sidebar({ selectedJobPosition, onSelectJob, onNewProject }: SidebarProps) {
  const { user, logout } = useAuth();
  const { data: jobs } = useJobs();
  const { data: usage } = useUsage();
  const navigate = useNavigate();
  const archiveJob = useArchiveJob();
  const [menu, setMenu] = useState<{ job: ProcessingJobList; pos: MenuPosition } | null>(null);
  const [infoJob, setInfoJob] = useState<ProcessingJobList | null>(null);
  const [renameJob, setRenameJob] = useState<ProcessingJobList | null>(null);
  const [deleteJob, setDeleteJob] = useState<ProcessingJobList | null>(null);

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, job: ProcessingJobList) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const MENU_W = 208;
    setMenu({
      job,
      pos: {
        top: rect.bottom + 4,
        left: Math.max(8, rect.right - MENU_W),
      },
    });
  };

  const completedJobs = jobs?.filter((j) => j.status === "COMPLETED") ?? [];
  const processingJobs = jobs?.filter((j) => j.status === "PROCESSING" || j.status === "PENDING") ?? [];
  const allJobs = jobs ?? [];
  const recentJobs = allJobs.slice(0, 7);

  return (
    <aside className="w-[220px] h-screen flex flex-col border-r bg-[#fafaf9] shrink-0">
      {/* Logo */}
      <div className="px-4 pt-5 pb-2">
        <h1 className="text-lg font-medium text-foreground tracking-tight">Eduarta</h1>
      </div>

      {/* New Project button */}
      <div className="px-3 pb-3">
        <button
          onClick={onNewProject}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-foreground text-background hover:opacity-90 transition-opacity"
        >
          <Plus className="w-4 h-4" />
          New project
        </button>
      </div>

      {/* Workspace nav */}
      <div className="px-3 pb-2">
        <p className="px-2 mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">Workspace</p>
        <button
          onClick={() => onSelectJob(-1)}
          className={`w-full flex items-center justify-between px-2 py-1.5 text-sm rounded-md transition-colors ${
            selectedJobPosition === -1 ? "bg-muted font-medium" : "hover:bg-muted/50"
          }`}
        >
          <span>All projects</span>
          <span className="text-xs text-muted-foreground">{allJobs.length}</span>
        </button>
        {processingJobs.length > 0 && (
          <button
            onClick={() => onSelectJob(-2)}
            className={`w-full flex items-center justify-between px-2 py-1.5 text-sm rounded-md transition-colors ${
              selectedJobPosition === -2 ? "bg-muted font-medium" : "hover:bg-muted/50"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" />
              Processing
            </span>
            <span className="text-xs text-muted-foreground">{processingJobs.length}</span>
          </button>
        )}
      </div>

      {/* Recent projects */}
      <div className="px-3 flex-1 overflow-y-auto min-h-0">
        <p className="px-2 mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">Recent</p>
        <div className="space-y-0.5">
          {recentJobs.map((job) => (
            <SidebarProjectItem
              key={job.id}
              job={job}
              isSelected={selectedJobPosition === job.position}
              onSelect={onSelectJob}
              onOpenMenu={openMenu}
            />
          ))}
        </div>
      </div>

      {/* Early Access */}
      <div className="px-3 py-3 border-t">
        <a
          href="mailto:hello@eduarta.com?subject=Eduarta%20Early%20Access%20Feedback"
          className="flex items-start gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50 transition-colors"
        >
          <Sparkles className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
          <div className="min-w-0">
            <div className="text-[11px] font-medium uppercase tracking-wider text-foreground/70">Early Access</div>
            <div className="text-[12px] text-muted-foreground leading-snug">Shape what&apos;s next →</div>
          </div>
        </a>
      </div>

      {/* Usage */}
      {usage && (() => {
        const used = usage.used ?? usage.used_minutes;
        const limit = usage.limit ?? usage.limit_minutes;
        const remaining = usage.remaining ?? Math.max(0, limit - used);
        const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
        const barColor = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-green-500";
        return (
          <div className="px-3 py-3 border-t">
            <div className="px-2 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                  Usage
                </span>
                <span className="text-[11px] text-muted-foreground tabular-nums">
                  {Math.round(used)} of {limit} min
                </span>
              </div>
              <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              {remaining < 30 && (
                <a
                  href="mailto:ag@eduarta.com?subject=Eduarta%20plan%20upgrade"
                  className="block text-[11px] text-primary hover:underline pt-0.5"
                >
                  {remaining <= 0 ? "Limit reached — Upgrade" : "Running low — Upgrade"}
                </a>
              )}
            </div>
          </div>
        );
      })()}

      {/* Context menu + dialogs (rendered as portals via fixed/dialog) */}
      {menu && (
        <ProjectContextMenu
          position={menu.pos}
          onClose={() => setMenu(null)}
          onInfo={() => { setInfoJob(menu.job); setMenu(null); }}
          onRename={() => { setRenameJob(menu.job); setMenu(null); }}
          onArchive={() => {
            const archivedJob = menu.job;
            archiveJob.mutate(archivedJob.id, {
              onSuccess: () => {
                if (selectedJobPosition === archivedJob.position) navigate("/");
              },
            });
            setMenu(null);
          }}
          onDelete={() => { setDeleteJob(menu.job); setMenu(null); }}
        />
      )}
      {infoJob && (
        <ProjectInfoModal
          job={infoJob}
          open
          onOpenChange={(o) => { if (!o) setInfoJob(null); }}
        />
      )}
      {renameJob && (
        <RenameProjectDialog
          job={renameJob}
          open
          onOpenChange={(o) => { if (!o) setRenameJob(null); }}
        />
      )}
      {deleteJob && (
        <DeleteProjectDialog
          job={deleteJob}
          open
          onOpenChange={(o) => { if (!o) setDeleteJob(null); }}
          onDeleted={() => {
            if (selectedJobPosition === deleteJob.position) navigate("/");
            setDeleteJob(null);
          }}
        />
      )}

      {/* User */}
      <div className="px-3 py-3 border-t">
        <div className="flex items-center gap-2 px-2">
          <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground uppercase">
            {user?.username?.substring(0, 2) ?? "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.username}</div>
            <button
              onClick={() => { if (window.confirm("Are you sure you want to log out?")) logout(); }}
              className="text-[11px] text-muted-foreground hover:text-foreground"
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}
